import { create } from 'zustand';
import api from '../services/api';
import { syncServerTheme } from '../theme/uiTheme';

export interface Branding {
  name: string;
  subtitle: string;
  logo: string;
  logo_icon: string;
  /** 完整版 logo（登入頁／關於頁）。後端 branding 端點不含此欄，靠 BRANDING_DEFAULT 合併保留。 */
  logo_full: string;
  /** 介面風格：claude＝橘暖（馬修預設）、vis＝東海 VIS、dark＝深色；全站一致，僅 dev 可改 */
  theme?: string;
}

// 東海冷凍 SUNWARD 官方 logo。VIS：等比縮放、不變形、不改色、不加外框/陰影、不裁切；透明底 PNG。
// logo=橫式短版(239×42) 側邊欄/導覽列；logo_full=完整版(443×144) 登入頁/關於頁；
// logo_icon=直式堆疊 lockup（紅圓+SUNWARD 紅字，867×806）供側邊欄收合用。
// 註：favicon / PWA 圖示另用純圓形 mark(/sunward-mark.png)——16~32px 下文字必糊，圓形才可辨識。
const BRANDING_DEFAULT: Branding = {
  name: 'AI冷藏庫滷水監控系統',
  subtitle: '東海冷凍',
  logo: '/sunward-horizontal.png',
  logo_icon: '/sunward-stacked.png',
  logo_full: '/sunward-full.png',
};

const ZOOM_LEVELS = [0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3];
const ZOOM_MIN = 0.7;
const ZOOM_MAX = 1.3;
const ZOOM_STEP = 0.1;
const ZOOM_STORAGE_KEY = 'cs_zoom_level';

function clampZoom(v: number): number {
  return Math.round(Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v)) * 10) / 10;
}

function loadZoom(): number {
  try {
    const v = parseFloat(localStorage.getItem(ZOOM_STORAGE_KEY) || '');
    if (!isNaN(v)) return clampZoom(v);
  } catch (e) { console.warn('[System] Failed to load zoom config:', e); }
  return 1.0;
}

interface SystemState {
  wsConnected: boolean;
  modbusConnected: boolean;
  mqttConnected: boolean;
  /** 各頁面的 active tab：path → tabKey。預設值由 tabRegistry 的 defaultTab 提供 */
  activeTabMap: Record<string, string>;
  branding: Branding;
  /** 畫面縮放倍率（0.7 ~ 1.5），持久化至 localStorage */
  zoomLevel: number;
  /** 精靈設備清單：null = 尚未設定（全部顯示），string[] = 已啟用設備 key */
  wizardConfig: string[] | null;
  setWsConnected: (connected: boolean) => void;
  setModbusConnected: (connected: boolean) => void;
  setMqttConnected: (connected: boolean) => void;
  setActiveTab: (path: string, tab: string) => void;
  fetchBranding: () => Promise<void>;
  fetchWizardConfig: () => Promise<void>;
  setZoomLevel: (level: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
}

const useSystemStore = create<SystemState>((set, get) => ({
  wsConnected: false,
  modbusConnected: false,
  mqttConnected: false,
  activeTabMap: {},
  branding: BRANDING_DEFAULT,
  zoomLevel: loadZoom(),
  wizardConfig: null,

  setWsConnected: (connected) => set({ wsConnected: connected }),
  setModbusConnected: (connected) => set({ modbusConnected: connected }),
  setMqttConnected: (connected) => set({ mqttConnected: connected }),
  setActiveTab: (path, tab) =>
    set((s) => ({ activeTabMap: { ...s.activeTabMap, [path]: tab } })),

  setZoomLevel: (level) => {
    const v = clampZoom(level);
    try { localStorage.setItem(ZOOM_STORAGE_KEY, String(v)); } catch { /* ignore */ }
    document.documentElement.style.setProperty('zoom', String(v));
    set({ zoomLevel: v });
  },
  zoomIn: () => {
    const cur = get().zoomLevel;
    const v = clampZoom(Math.round((cur + ZOOM_STEP) * 10) / 10);
    try { localStorage.setItem(ZOOM_STORAGE_KEY, String(v)); } catch { /* ignore */ }
    document.documentElement.style.setProperty('zoom', String(v));
    set({ zoomLevel: v });
  },
  zoomOut: () => {
    const cur = get().zoomLevel;
    const v = clampZoom(Math.round((cur - ZOOM_STEP) * 10) / 10);
    try { localStorage.setItem(ZOOM_STORAGE_KEY, String(v)); } catch { /* ignore */ }
    document.documentElement.style.setProperty('zoom', String(v));
    set({ zoomLevel: v });
  },
  resetZoom: () => {
    try { localStorage.setItem(ZOOM_STORAGE_KEY, '1'); } catch { /* ignore */ }
    document.documentElement.style.setProperty('zoom', '1');
    set({ zoomLevel: 1.0 });
  },

  fetchBranding: async () => {
    try {
      const res = await api.get('/system/branding');
      if (res.data?.success) {
        set({ branding: { ...BRANDING_DEFAULT, ...res.data.data } });
        // 介面風格以後端為準（全站一致）；跟本機開機快取不同時會重新載入一次
        syncServerTheme(res.data.data?.theme);
      }
    } catch {
      // 沿用預設值
    }
  },

  fetchWizardConfig: async () => {
    try {
      const res = await api.get('/wizard/config');
      if (res.data?.success) {
        set({ wizardConfig: res.data.data.enabled_devices ?? null });
      }
    } catch {
      // 沿用 null（全部顯示）
    }
  },
}));

export { ZOOM_MIN, ZOOM_MAX, ZOOM_LEVELS };
export default useSystemStore;
